"use client";
import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { StripeElementLocale, loadStripe } from "@stripe/stripe-js";
import { useEffect, useRef, useState } from "react";

import { Block, Flex } from "/components/Layout";
import Button from "/components/Button";

import styles from "components/Locations/Locations.module.scss";

import { stripePublicKey } from "/constants";

const stripePromise = loadStripe(stripePublicKey(), {
  locale: "nb"
});

type CheckoutFormProps = {
  prevStep?: () => void;
  title: string;
  returnUrl: string;
  ctaTitle?: string;
};

const CheckoutForm = ({ prevStep, title, returnUrl, ctaTitle }: CheckoutFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessages, setErrorMessages] = useState<string | undefined>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const creditCardFormRef = useRef(null);

  useEffect(() => {
    // @ts-ignore
    creditCardFormRef?.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, []);

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    setIsSubmitting(true);

    const result = await stripe.confirmSetup({
      elements,
      confirmParams: {
        // Return URL where the customer should be redirected after the SetupIntent is confirmed.
        return_url: returnUrl
      }
    });

    if (result.error) {
      // Show error to your customer (for example, payment details incomplete)
      setErrorMessages(result.error.message);
    }

    setIsSubmitting(false);
  };

  return (
    <Block top={8}>
      <form ref={creditCardFormRef} onSubmit={handleSubmit}>
        <div className={styles.container}>
          <Block bottom={6}>
            <p className="body-text">{title}</p>
          </Block>
          <PaymentElement />
          {errorMessages && (
            <Block top={4}>
              <p className="text-label color--red">{errorMessages}</p>
            </Block>
          )}
        </div>

        <Block top={6} bottom={2}>
          <Flex justify="end">
            {prevStep && (
              <Block right={4}>
                <Button onClick={prevStep} type="submit" secondary>
                  Tilbake
                </Button>
              </Block>
            )}

            <Button isSubmitting={isSubmitting} disabled={isSubmitting} type="submit" primary>
              {ctaTitle}
            </Button>
          </Flex>
        </Block>
      </form>
    </Block>
  );
};

function Provider(props: CheckoutFormProps & { clientSecret: string }) {
  const options: {
    clientSecret: string;
    locale: StripeElementLocale;
  } = {
    // Passing the client secret obtained from the server
    clientSecret: props.clientSecret,
    locale: "nb"
  };
  return (
    <Elements stripe={stripePromise} options={options}>
      <CheckoutForm {...props} />
    </Elements>
  );
}

export default Provider;
